import { produce } from 'immer';
import { without } from 'lodash';
import * as actions from 'store/actions';
import {
  AbridgedAsset,
  AssetsSortOrder,
  AssetsState,
  PartialAbridgedAsset,
  AssetFromSync,
} from 'store/assets/types';
import { createReducer } from 'typesafe-actions';

const EMPTY_ASSETS_LIST_ITEM: AbridgedAsset = {
  id: '',
  reference: '',
  organisation_id: '',
  dv: {},
  dvl: {},
  assessment_summary: {
    due: [],
    list: [],
  },
  compliance_summary: {},
  details: {
    name: '',
  },
  downloadFiles: [],
  downloadThumbnails: [],
};

const initialState: AssetsState = {
  assets_list: {},
  chosen: [],
  files: {},
  filter: {
    assessment_statuses: [],
    organisations: [],
    search: '',
  },
  loaded: {},
  loadedCount: {},
  sort: AssetsSortOrder.ASSESSMENTDUEASC,
};

const convertAssetToAssetListItem = (
  asset: AssetFromSync,
): PartialAbridgedAsset => {
  const listItem: PartialAbridgedAsset = {
    assessment_summary: asset.assessment_summary,
    compliance_summary: asset.compliance_summary,
    details: asset.details,
    downloadFiles: asset.downloadFiles,
    downloadThumbnails: asset.downloadThumbnails,
    id: asset.id,
    organisation_id: asset.organisation_id,
    reference: asset.reference,
  };

  for (const key of Object.keys(listItem)) {
    const k = key as keyof PartialAbridgedAsset;
    if (typeof listItem[k] === 'undefined') {
      delete listItem[k];
    }
  }
  return listItem;
};

const mergeAssetPayloadToListItem = (
  prev: AbridgedAsset | undefined,
  next: AssetFromSync,
): AbridgedAsset => {
  return {
    ...EMPTY_ASSETS_LIST_ITEM,
    ...prev,
    ...convertAssetToAssetListItem(next),
    // Set Data Versions
    dvl: {
      ...prev?.dvl,
      ...next.dv,
    },
    dv: {
      ...prev?.dv,
      ...next.dv,
    },
  } as AbridgedAsset;
};

export const assetReducer = createReducer(initialState)
  .handleAction(actions.SIGN_OUT.success, () => initialState)
  .handleAction(actions.RESET_DEV_DATA, (state) => ({
    ...state,
    chosen: [],
    loaded: {},
    loadedCount: {},
  }))
  .handleAction(
    actions.doAssetUpdateListItemComplianceSummary,
    (state, action) =>
      produce(state, (draft) => {
        const { assetId, complianceId, complianceSummary } = action.payload;
        const draftAsset = draft.assets_list[assetId];
        if (draftAsset) {
          draftAsset.compliance_summary[complianceId] = complianceSummary;
        }
      }),
  )
  .handleAction(actions.UPDATE_ASSET_LIST, (state, action) =>
    produce(state, (draft) => {
      for (const assetId of Object.keys(action.payload.assets)) {
        draft.assets_list[assetId] = mergeAssetPayloadToListItem(
          state.assets_list[assetId],
          action.payload.assets[assetId]!,
        );
      }
    }),
  )
  .handleAction(actions.LOAD_ASSET.success, (state, action) => ({
    ...state,
    loadedCount: {
      ...state.loadedCount,
      [action.payload]: (state.loadedCount[action.payload] || 0) + 1,
    },
  }))
  .handleAction(actions.SET_LOADED_ASSET, (state, action) => ({
    ...state,
    loaded: {
      ...state.loaded,
      [action.payload.id]: action.payload,
    },
  }))
  .handleAction(actions.UNSET_LOADED_ASSET, (state, action) =>
    produce(state, (draft) => {
      const newCount = Math.max(
        (state.loadedCount[action.payload] || 1) - 1,
        0,
      );
      if (!newCount) {
        delete draft.loaded[action.payload];
        delete draft.loadedCount[action.payload];
      } else {
        draft.loadedCount[action.payload] = newCount;
      }
    }),
  )
  .handleAction(actions.SET_ASSETS_SORT, (state, action) => ({
    ...state,
    sort: action.payload,
  }))
  .handleAction(actions.SET_ASSETS_FILTER, (state, action) =>
    produce(state, (draft) => {
      for (const key of Object.keys(action.payload)) {
        const k = key as keyof typeof action.payload;
        const value = action.payload[k];
        if (value !== undefined) {
          draft.filter[k] = value as string & string[];
        }
      }
    }),
  )
  .handleAction(actions.TOGGLE_CHOSEN_ASSET, (state, action) => ({
    ...state,
    chosen: state.chosen.includes(action.payload)
      ? without(state.chosen, action.payload)
      : [...state.chosen, action.payload],
  }))
  .handleAction(actions.CHOOSE_ASSETS, (state, action) => ({
    ...state,
    chosen: action.payload,
  }))
  .handleAction(actions.CLEAR_CHOSEN_ASSETS, (state) => ({
    ...state,
    chosen: initialState.chosen,
  }))
  .handleAction(actions.ENQUEUE_ASSET_DELETE, (state, action) => ({
    ...state,
    chosen: without(state.chosen, ...action.payload),
  }))
  .handleAction(actions.DELETE_ASSETS, (state, action) =>
    produce(state, (draft) => {
      const assetIds = action.payload.ids;

      for (const assetId of assetIds) {
        delete draft.assets_list[assetId];
        delete draft.loaded[assetId];
        delete draft.loadedCount[assetId];
      }
    }),
  )
  .handleAction(actions.DELETE_ORGANISATIONS, (state, action) => ({
    ...state,
    filter: {
      ...state.filter,
      organisations: state.filter.organisations.filter(
        (organisationId) => !action.payload.ids.includes(organisationId),
      ),
    },
  }));
